import type {
  event_active_media_failed,
  event_active_share_change,
  event_annotation_privilege_change,
  event_annotation_redo_status,
  event_annotation_undo_status,
  event_annotation_viewer_draw_request,
  event_audio_active_speaker,
  event_audio_statistic_data_change,
  event_auto_play_audio_failed,
  event_bo_ask_for_help,
  event_bo_ask_for_help_response,
  event_bo_broadcast_message,
  event_bo_closing_room_countdown,
  event_bo_invite_to_join,
  event_bo_main_session_change,
  event_bo_room_countdown,
  event_bo_room_state_change,
  event_bo_room_time_up,
  event_broadcast_streaming_status,
  event_caption_enable,
  event_caption_host_disable,
  event_caption_language_lock,
  event_caption_message,
  event_caption_status,
  event_chat_file_download_progress,
  event_chat_file_upload_progress,
  event_chat_privilege_change,
  event_chat_received_message,
  event_command_channel_message,
  event_command_channel_status,
  event_connection_change,
  event_crc_device_call_state_change,
  event_current_audio_change,
  event_current_audio_level_change,
  event_device_change,
  event_device_permission_change,
  event_dial_out_change,
  event_far_end_camera_capability_change,
  event_far_end_camera_in_control_change,
  event_far_end_camera_request,
  event_far_end_camera_response,
  event_host_ask_unmute_audio,
  event_incoming_live_stream_status,
  event_individual_recording_change,
  event_live_stream_status,
  event_media_sdk_change,
  event_meeting_query_change,
  event_network_quality_change,
  event_passively_stop_share,
  event_peer_share_state_change,
  event_peer_video_state_change,
  event_peer_whiteboard_state_change,
  event_real_time_media_streams_status_change,
  event_recording_change,
  event_remote_control_app_status_change,
  event_remote_control_approved_change,
  event_remote_control_clipboard_change,
  event_remote_control_controlled_status_change,
  event_remote_control_in_control_change,
  event_remote_control_request_change,
  event_share_audio_change,
  event_share_camera_approve_change,
  event_share_camera_request,
  event_share_camera_status,
  event_share_can_see_screen,
  event_share_content_change,
  event_share_content_dimension_change,
  event_share_content_screenshot_taken,
  event_share_privilege_change,
  event_share_statistic_data_change,
  event_smart_summary_change,
  event_speaking_while_muted,
  event_subsession_broadcast_voice,
  event_subsession_invite_to_back_to_main_session,
  event_subsession_user_update,
  event_system_resource_usage_change,
  event_user_add,
  event_user_remove,
  event_user_update,
  event_video_active_change,
  event_video_aspect_ratio_change,
  event_video_capturing_change,
  event_video_cell_detailed_change,
  event_video_dimension_change,
  event_video_screenshot_taken,
  event_video_spotlight_change,
  event_video_statistic_data_change,
  event_video_vb_preload_change,
  event_voice_translator_started,
  event_voice_translator_unsupported_language_pair,
  event_webrtc_stats_report_data_change,
  event_whiteboard_status_change,
} from '@zoom/videosdk';

import type { ProjectionFieldsFor } from '../../utils/projection';

declare const handlerType: unique symbol;

type HandlerPayload<Handler> = Handler extends (...args: infer Args) => unknown ? Args[number] : never;
type MapValue<Value> = Value extends ReadonlyMap<unknown, infer Item> ? Item : never;

type EventProjection<Handler> =
  | { mode: 'none' }
  | { mode: 'scalar' }
  | { mode: 'pick'; fields: ProjectionFieldsFor<HandlerPayload<Handler>> }
  | {
      mode: 'pick-map-values';
      fields: ProjectionFieldsFor<MapValue<HandlerPayload<Handler>>>;
    };

type EventConfig<Handler> = EventProjection<Handler> & {
  [handlerType]?: Handler;
};

function defineEvent<Handler>(projection: EventProjection<Handler>): EventConfig<Handler> {
  return projection;
}

const AUDIO_STATISTIC_LOG_FIELDS = {
  data: {
    avg_loss: true,
    encoding: true,
    jitter: true,
    max_loss: true,
    rtt: true,
    sample_rate: true,
  },
  type: true,
} as const satisfies ProjectionFieldsFor<HandlerPayload<typeof event_audio_statistic_data_change>>;

const VIDEO_STATISTIC_LOG_FIELDS = {
  data: {
    avg_loss: true,
    bitrate: true,
    encoding: true,
    fps: true,
    height: true,
    jitter: true,
    max_loss: true,
    rtt: true,
    width: true,
  },
  type: true,
} as const satisfies ProjectionFieldsFor<HandlerPayload<typeof event_video_statistic_data_change>>;

const SHARE_STATISTIC_LOG_FIELDS = {
  data: {
    avg_loss: true,
    bitrate: true,
    encoding: true,
    fps: true,
    height: true,
    jitter: true,
    max_loss: true,
    rtt: true,
    width: true,
  },
  type: true,
} as const satisfies ProjectionFieldsFor<HandlerPayload<typeof event_share_statistic_data_change>>;

const PARTICIPANT_LOG_FIELDS = {
  audio: true,
  bShareAudioOn: true,
  bShareToSubsession: true,
  bVideoOn: true,
  bVideoShare: true,
  isAllowIndividualRecording: true,
  isHost: true,
  isInFailover: true,
  isManager: true,
  isPhoneUser: true,
  isRtmpUser: true,
  isSpeakerOnly: true,
  isVideoConnect: true,
  isVoiceTranslatorOn: true,
  muted: true,
  sharerOn: true,
  sharerPause: true,
  subsessionId: true,
  userGuid: true,
  userId: true,
  voiceListeningLanguageCode: true,
  voiceSpeakingLanguageCode: true,
  voiceTimbre: true,
} as const satisfies ProjectionFieldsFor<HandlerPayload<typeof event_user_add>>;

const WEBRTC_STATISTIC_LOG_FIELDS = {
  audioLevel: true,
  bytesReceived: true,
  bytesSent: true,
  frameHeight: true,
  frameWidth: true,
  framesPerSecond: true,
  isSharing: true,
  jitter: true,
  kind: true,
  nodeId: true,
  packetsLost: true,
  packetsReceived: true,
  packetsSent: true,
  roundTripTime: true,
  timestamp: true,
  totalAudioEnergy: true,
  totalRoundTripTime: true,
  totalSamplesDuration: true,
  type: true,
} as const satisfies ProjectionFieldsFor<MapValue<HandlerPayload<typeof event_webrtc_stats_report_data_change>>>;

export const ZOOM_VIDEO_CLIENT_EVENTS = {
  'active-media-failed': defineEvent<typeof event_active_media_failed>({
    mode: 'pick',
    fields: { code: true, message: true, type: true },
  }),
  'active-share-change': defineEvent<typeof event_active_share_change>({
    mode: 'pick',
    fields: { state: true, userId: true },
  }),
  'active-speaker': defineEvent<typeof event_audio_active_speaker>({ mode: 'pick', fields: { userId: true } }),
  'annotation-privilege-change': defineEvent<typeof event_annotation_privilege_change>({
    mode: 'pick',
    fields: { isAnnotationEnabled: true, userId: true },
  }),
  'annotation-redo-status': defineEvent<typeof event_annotation_redo_status>({
    mode: 'pick',
    fields: { presenterId: true, status: true },
  }),
  'annotation-undo-status': defineEvent<typeof event_annotation_undo_status>({
    mode: 'pick',
    fields: { presenterId: true, status: true },
  }),
  'annotation-viewer-draw-request': defineEvent<typeof event_annotation_viewer_draw_request>({
    mode: 'pick',
    fields: { userId: true },
  }),
  'audio-statistic-data-change': defineEvent<typeof event_audio_statistic_data_change>({
    mode: 'pick',
    fields: AUDIO_STATISTIC_LOG_FIELDS,
  }),
  'auto-play-audio-failed': defineEvent<typeof event_auto_play_audio_failed>({ mode: 'none' }),
  'broadcast-streaming-status': defineEvent<typeof event_broadcast_streaming_status>({
    mode: 'pick',
    fields: { status: true },
  }),
  'caption-enable': defineEvent<typeof event_caption_enable>({ mode: 'scalar' }),
  'caption-host-disable': defineEvent<typeof event_caption_host_disable>({ mode: 'scalar' }),
  'caption-language-lock': defineEvent<typeof event_caption_language_lock>({ mode: 'scalar' }),
  'caption-message': defineEvent<typeof event_caption_message>({
    mode: 'pick',
    fields: { done: true, language: true, msgId: true, source: true, timestamp: true, userId: true },
  }),
  'caption-status': defineEvent<typeof event_caption_status>({
    mode: 'pick',
    fields: {
      autoCaption: true,
      lang: true,
      language: true,
      sessionLang: true,
      sessionLanguage: true,
      translationStarted: true,
    },
  }),
  'chat-file-download-progress': defineEvent<typeof event_chat_file_download_progress>({
    mode: 'pick',
    fields: { fileSize: true, id: true, progress: true, senderGuid: true, senderId: true, status: true },
  }),
  'chat-file-upload-progress': defineEvent<typeof event_chat_file_upload_progress>({
    mode: 'pick',
    fields: { fileSize: true, progress: true, receiverGuid: true, receiverId: true, status: true },
  }),
  'chat-on-message': defineEvent<typeof event_chat_received_message>({
    mode: 'pick',
    fields: {
      file: {
        download: { progress: true, status: true },
        size: true,
        upload: { progress: true, status: true },
      },
      id: true,
      receiver: { userGuid: true, userId: true },
      sender: { userGuid: true, userId: true },
      timestamp: true,
    },
  }),
  'chat-privilege-change': defineEvent<typeof event_chat_privilege_change>({
    mode: 'pick',
    fields: { chatPrivilege: true },
  }),
  'closing-subsession-countdown': defineEvent<typeof event_bo_closing_room_countdown>({
    mode: 'pick',
    fields: { countdown: true },
  }),
  'command-channel-message': defineEvent<typeof event_command_channel_message>({
    mode: 'pick',
    fields: { msgid: true, senderGuid: true, senderId: true, senderUserKey: true, timestamp: true },
  }),
  'command-channel-status': defineEvent<typeof event_command_channel_status>({ mode: 'scalar' }),
  'connection-change': defineEvent<typeof event_connection_change>({
    mode: 'pick',
    fields: { reason: true, state: true },
  }),
  'crc-call-out-state-change': defineEvent<typeof event_crc_device_call_state_change>({
    mode: 'pick',
    fields: { code: true, protocol: true, uuid: true },
  }),
  'current-audio-change': defineEvent<typeof event_current_audio_change>({
    mode: 'pick',
    fields: { action: true, source: true, type: true },
  }),
  'current-audio-level-change': defineEvent<typeof event_current_audio_level_change>({
    mode: 'pick',
    fields: { level: true },
  }),
  'device-change': defineEvent<typeof event_device_change>({ mode: 'none' }),
  'device-permission-change': defineEvent<typeof event_device_permission_change>({
    mode: 'pick',
    fields: { name: true, state: true },
  }),
  'dialout-state-change': defineEvent<typeof event_dial_out_change>({
    mode: 'pick',
    fields: { code: true, userId: true, uuid: true },
  }),
  'far-end-camera-capability-change': defineEvent<typeof event_far_end_camera_capability_change>({
    mode: 'pick',
    fields: { ptz: { pan: true, tilt: true, zoom: true }, userId: true },
  }),
  'far-end-camera-in-control-change': defineEvent<typeof event_far_end_camera_in_control_change>({
    mode: 'pick',
    fields: { isControlled: true, userId: true },
  }),
  'far-end-camera-request-control': defineEvent<typeof event_far_end_camera_request>({
    mode: 'pick',
    fields: { currentControllingUserId: true, userId: true },
  }),
  'far-end-camera-response-control': defineEvent<typeof event_far_end_camera_response>({
    mode: 'pick',
    fields: { isApproved: true, reason: true, userId: true },
  }),
  'host-ask-unmute-audio': defineEvent<typeof event_host_ask_unmute_audio>({
    mode: 'pick',
    fields: { reason: true },
  }),
  'incoming-live-stream-status': defineEvent<typeof event_incoming_live_stream_status>({
    mode: 'pick',
    fields: { isRTMPConnected: true, isStreamPushed: true, streamId: true },
  }),
  'individual-recording-change': defineEvent<typeof event_individual_recording_change>({
    mode: 'pick',
    fields: { status: true, userId: true },
  }),
  'live-stream-status': defineEvent<typeof event_live_stream_status>({ mode: 'scalar' }),
  'main-session-user-updated': defineEvent<typeof event_bo_main_session_change>({ mode: 'none' }),
  'media-sdk-change': defineEvent<typeof event_media_sdk_change>({
    mode: 'pick',
    fields: { action: true, result: true, type: true },
  }),
  'meeting-query-status-change': defineEvent<typeof event_meeting_query_change>({
    mode: 'pick',
    fields: { status: true, support: true },
  }),
  'network-quality-change': defineEvent<typeof event_network_quality_change>({
    mode: 'pick',
    fields: { level: true, type: true, userId: true },
  }),
  'passively-stop-share': defineEvent<typeof event_passively_stop_share>({ mode: 'scalar' }),
  'peer-share-state-change': defineEvent<typeof event_peer_share_state_change>({
    mode: 'pick',
    fields: { action: true, userId: true },
  }),
  'peer-video-state-change': defineEvent<typeof event_peer_video_state_change>({
    mode: 'pick',
    fields: { action: true, userId: true },
  }),
  'peer-whiteboard-state-change': defineEvent<typeof event_peer_whiteboard_state_change>({
    mode: 'pick',
    fields: { action: true, userId: true },
  }),
  'real-time-media-streams-status-change': defineEvent<typeof event_real_time_media_streams_status_change>({
    mode: 'pick',
    fields: { status: true },
  }),
  'recording-change': defineEvent<typeof event_recording_change>({ mode: 'scalar' }),
  'remote-control-app-status-change': defineEvent<typeof event_remote_control_app_status_change>({ mode: 'scalar' }),
  'remote-control-approved-change': defineEvent<typeof event_remote_control_approved_change>({
    mode: 'pick',
    fields: { state: true },
  }),
  'remote-control-clipboard-change': defineEvent<typeof event_remote_control_clipboard_change>({
    mode: 'pick',
    fields: { x: true, y: true },
  }),
  'remote-control-controlled-status-change': defineEvent<typeof event_remote_control_controlled_status_change>({
    mode: 'scalar',
  }),
  'remote-control-in-control-change': defineEvent<typeof event_remote_control_in_control_change>({
    mode: 'pick',
    fields: { isControlling: true },
  }),
  'remote-control-request-change': defineEvent<typeof event_remote_control_request_change>({
    mode: 'pick',
    fields: { isSharingEntireScreen: true, userId: true },
  }),
  'share-audio-change': defineEvent<typeof event_share_audio_change>({ mode: 'pick', fields: { state: true } }),
  'share-camera-approve-change': defineEvent<typeof event_share_camera_approve_change>({
    mode: 'pick',
    fields: { requestId: true, status: true, userId: true },
  }),
  'share-camera-request': defineEvent<typeof event_share_camera_request>({
    mode: 'pick',
    fields: { requestId: true, shareSource: true, startAnnotation: true, userId: true },
  }),
  'share-camera-status': defineEvent<typeof event_share_camera_status>({
    mode: 'pick',
    fields: { requestId: true, status: true, userId: true },
  }),
  'share-can-see-screen': defineEvent<typeof event_share_can_see_screen>({ mode: 'none' }),
  'share-content-change': defineEvent<typeof event_share_content_change>({ mode: 'pick', fields: { userId: true } }),
  'share-content-dimension-change': defineEvent<typeof event_share_content_dimension_change>({
    mode: 'pick',
    fields: { height: true, type: true, userId: true, width: true },
  }),
  'share-content-screenshot-taken': defineEvent<typeof event_share_content_screenshot_taken>({
    mode: 'pick',
    fields: { userId: true },
  }),
  'share-privilege-change': defineEvent<typeof event_share_privilege_change>({
    mode: 'pick',
    fields: { privilege: true },
  }),
  'share-statistic-data-change': defineEvent<typeof event_share_statistic_data_change>({
    mode: 'pick',
    fields: SHARE_STATISTIC_LOG_FIELDS,
  }),
  'speaking-while-muted': defineEvent<typeof event_speaking_while_muted>({ mode: 'none' }),
  'subsession-ask-for-help': defineEvent<typeof event_bo_ask_for_help>({
    mode: 'pick',
    fields: { subsessionId: true, userId: true },
  }),
  'subsession-ask-for-help-response': defineEvent<typeof event_bo_ask_for_help_response>({
    mode: 'pick',
    fields: { result: true },
  }),
  'subsession-broadcast-message': defineEvent<typeof event_bo_broadcast_message>({ mode: 'none' }),
  'subsession-broadcast-voice': defineEvent<typeof event_subsession_broadcast_voice>({
    mode: 'pick',
    fields: { status: true },
  }),
  'subsession-countdown': defineEvent<typeof event_bo_room_countdown>({
    mode: 'pick',
    fields: { countdown: true },
  }),
  'subsession-invite-back-to-main-session': defineEvent<typeof event_subsession_invite_to_back_to_main_session>({
    mode: 'pick',
    fields: { inviterGuid: true, inviterId: true },
  }),
  'subsession-invite-to-join': defineEvent<typeof event_bo_invite_to_join>({
    mode: 'pick',
    fields: { subsessionId: true },
  }),
  'subsession-state-change': defineEvent<typeof event_bo_room_state_change>({
    mode: 'pick',
    fields: { status: true },
  }),
  'subsession-time-up': defineEvent<typeof event_bo_room_time_up>({ mode: 'none' }),
  'subsession-user-update': defineEvent<typeof event_subsession_user_update>({
    mode: 'pick',
    fields: {
      audio: true,
      bShareAudioOn: true,
      bVideoOn: true,
      isTalking: true,
      muted: true,
      sharerOn: true,
      subsessionId: true,
      userGuid: true,
      userId: true,
    },
  }),
  'summary-status-change': defineEvent<typeof event_smart_summary_change>({
    mode: 'pick',
    fields: { status: true, support: true },
  }),
  'system-resource-usage-change': defineEvent<typeof event_system_resource_usage_change>({
    mode: 'pick',
    fields: { cpu_usage: { system_cpu_pressure_level: true } },
  }),
  'user-added': defineEvent<typeof event_user_add>({ mode: 'pick', fields: PARTICIPANT_LOG_FIELDS }),
  'user-removed': defineEvent<typeof event_user_remove>({ mode: 'pick', fields: PARTICIPANT_LOG_FIELDS }),
  'user-updated': defineEvent<typeof event_user_update>({ mode: 'pick', fields: PARTICIPANT_LOG_FIELDS }),
  'video-active-change': defineEvent<typeof event_video_active_change>({
    mode: 'pick',
    fields: { state: true, userId: true },
  }),
  'video-aspect-ratio-change': defineEvent<typeof event_video_aspect_ratio_change>({
    mode: 'pick',
    fields: { aspectRatio: true, userId: true },
  }),
  'video-capturing-change': defineEvent<typeof event_video_capturing_change>({
    mode: 'pick',
    fields: { state: true },
  }),
  'video-detailed-data-change': defineEvent<typeof event_video_cell_detailed_change>({
    mode: 'pick',
    fields: { fps: true, height: true, quality: true, userId: true, width: true },
  }),
  'video-dimension-change': defineEvent<typeof event_video_dimension_change>({
    mode: 'pick',
    fields: { height: true, type: true, width: true },
  }),
  'video-screenshot-taken': defineEvent<typeof event_video_screenshot_taken>({
    mode: 'pick',
    fields: { userId: true },
  }),
  'video-spotlight-change': defineEvent<typeof event_video_spotlight_change>({
    mode: 'pick',
    fields: { spotlightList: { userId: true } },
  }),
  'video-statistic-data-change': defineEvent<typeof event_video_statistic_data_change>({
    mode: 'pick',
    fields: VIDEO_STATISTIC_LOG_FIELDS,
  }),
  'video-virtual-background-preload-change': defineEvent<typeof event_video_vb_preload_change>({
    mode: 'pick',
    fields: { isReady: true },
  }),
  'voice-translator-started': defineEvent<typeof event_voice_translator_started>({
    mode: 'pick',
    fields: { language: true, timbreIndex: true },
  }),
  'voice-translator-unsupported-language-pair': defineEvent<typeof event_voice_translator_unsupported_language_pair>({
    mode: 'pick',
    fields: { listeningLanguage: true, speakingLanguage: true, userId: true },
  }),
  'webrtc-stats-report-data-change': defineEvent<typeof event_webrtc_stats_report_data_change>({
    mode: 'pick-map-values',
    fields: WEBRTC_STATISTIC_LOG_FIELDS,
  }),
  'whiteboard-status-change': defineEvent<typeof event_whiteboard_status_change>({ mode: 'scalar' }),
} as const;

export type VideoClientEvent = keyof typeof ZOOM_VIDEO_CLIENT_EVENTS;

export const ALL_ZOOM_EVENTS = Object.keys(ZOOM_VIDEO_CLIENT_EVENTS) as VideoClientEvent[];

type HandlerOf<Config> = Config extends EventConfig<infer Handler> ? Handler : never;

export type ZoomVideoClientEventHandlerMap = {
  [Event in VideoClientEvent]: HandlerOf<(typeof ZOOM_VIDEO_CLIENT_EVENTS)[Event]>;
};

export type VideoClientEventHandler<T extends VideoClientEvent> = ZoomVideoClientEventHandlerMap[T];
